// กติกาที่นั่งแคชเชียร์ — Main ได้ 1 เครื่อง, Second ได้ไม่เกิน N เครื่อง
//
// ไฟล์นี้เป็นฟังก์ชันล้วน ห้าม import firebase / pinia / nuxt เด็ดขาด
// scripts/test-cashier-seat.mjs transpile ไฟล์นี้ไฟล์เดียวไปรันใน node ตรง ๆ
// ส่วนที่คุยกับ RTDB อยู่ที่ modules/seat/services/seatRtdb.ts
//
// รูปข้อมูลใน RTDB stores/{storeId}/cashier_seats:
//   main:   { deviceId, name, claimedAt, lastSeen, claimedAtIso, lastSeenIso }
//   second: { {deviceId}: { name, claimedAt, lastSeen, claimedAtIso, lastSeenIso } }
//   kicks:  { {deviceId ที่โดนแย่ง/เตะ}: { byId, byName, mode, at, atIso } }
// claimedAt = เข้าใช้ที่นั่งนี้ตั้งแต่เมื่อไร, lastSeen = ออนไลน์ล่าสุด (heartbeat เขียนด้วย
// serverTimestamp()) — ทั้งคู่เป็น epoch ms โชว์ในหน้า static ให้ตัดสินใจว่าจะแย่งเครื่องไหน
// *Iso = ค่าเดียวกันแบบคนอ่านออก (ไว้ดูใน Firebase console) — ตัดสินจากตัวเลขเสมอ ห้ามอ่าน *Iso
// kicks = ใครแย่ง/เตะเครื่องไหนออก เครื่องที่โดนจะได้เห็น Computer ID คนที่เข้ามา
// ไว้แจ้ง support บล็อกถูกเครื่อง (เขียนใน transaction เดียวกับการแย่ง)

/** ค่าเดียวกับ RunMode.MAIN_CASHIER / RunMode.CASHIER_2ND ใน interface/Pos.ts */
export const SEAT_MODE_MAIN = "main_cashier";
export const SEAT_MODE_SECOND = "cashier_2nd";
export type SeatMode = typeof SEAT_MODE_MAIN | typeof SEAT_MODE_SECOND;

/** heartbeat ทุก 60 วินาที */
export const SEAT_HEARTBEAT_MS = 60 * 1000;
/** เงียบเกิน 5 นาทีถือว่าเครื่องหลุด — ครอบ reload / เน็ตกระตุก / iPad พักจอสั้น ๆ */
export const SEAT_STALE_MS = 5 * 60 * 1000;

/** ค่า cashier_2nd เก่าที่เป็น true / ค่าที่อ่านไม่ออก = 1 เครื่อง */
export const CASHIER_2ND_LEGACY_QUOTA = 1;

/** บันทึกการโดนแย่งเก็บไว้ไม่เกินนี้ ไม่งั้น node โตไปเรื่อย ๆ */
export const SEAT_KICK_TTL_MS = 24 * 60 * 60 * 1000;

/** ใครแย่ง / เตะเครื่องนี้ออก */
export interface SeatKick {
  /** Computer ID ของเครื่องที่เข้ามาแทน */
  byId: string;
  byName: string;
  mode: SeatMode;
  at: number;
  /** at แบบคนอ่านออก — build() เติมให้ */
  atIso?: string | null;
}

export interface SeatHolder {
  /** computerUuid */
  deviceId: string;
  name: string;
  /** เข้าใช้ที่นั่งนี้ตั้งแต่เมื่อไร */
  claimedAt: number | null;
  /** ออนไลน์ล่าสุด */
  lastSeen: number | null;
  alive: boolean;
}

export type ClaimResult =
  /** จองได้ (หรือเป็นของเครื่องนี้อยู่แล้ว) */
  | "ok"
  /** Main เป็นของเครื่องอื่น ต้องให้ผู้ใช้ยืนยันก่อนเขียนทับ (force) */
  | "main_taken"
  /** Second เต็ม ต้องให้ผู้ใช้เลือกเตะเครื่องใดเครื่องหนึ่ง (kickId) */
  | "second_full"
  /** ร้านไม่มีสิทธิ์ Second เลย (cashier_2nd = 0 / false) */
  | "no_quota";

export interface ClaimOptions {
  mode: SeatMode;
  /** computerUuid ของเครื่องนี้ */
  me: string;
  /** ชื่อเครื่องที่โชว์ให้คนอื่นเห็น */
  name: string;
  /** จำนวน Second ที่ร้านมีสิทธิ์ (ไม่ใช้กับ Main) */
  quota: number;
  /** เวลา server ตอนนี้ (epoch ms) */
  now: number;
  /** ยืนยันแล้วว่าจะเขียนทับ Main ของเครื่องอื่น */
  force?: boolean;
  /** ยืนยันแล้วว่าจะเตะ Second เครื่องนี้ออก */
  kickId?: string | null;
}

export interface ClaimDecision {
  result: ClaimResult;
  /** ค่าที่จะเขียนกลับเมื่อ result = ok */
  next: any;
  /** เครื่องที่ถือที่นั่งอยู่ (ไว้โชว์ใน dialog) */
  holders: SeatHolder[];
  /** จำนวน Second ที่ยังไม่หลุด หลังตัดสินรอบนี้ */
  used: number;
  quota: number;
}

/**
 * แปลงค่า setting `cashier_2nd` เป็นจำนวนเครื่อง Second ที่ร้านเปิดได้
 *
 * | ค่าที่เก็บ                   | ผลลัพธ์ |
 * |------------------------------|---------|
 * | ไม่มีค่า / null / false / 0  | 0       |
 * | true (ของเก่า)               | 1       |
 * | 2 หรือ "2"                   | 2       |
 * | อ่านไม่ออกแต่มีค่าอยู่        | 1       |
 *
 * ห้ามคืน Infinity ไม่ว่ากรณีใด — true เคยแปลว่า "ไม่จำกัด" ซึ่งคือปัญหาที่กำลังแก้
 */
export function resolveCashier2ndQuota(value: unknown): number {
  if (value === null || value === undefined) return 0;

  if (typeof value === "boolean") {
    return value ? CASHIER_2ND_LEGACY_QUOTA : 0;
  }

  if (typeof value === "number") {
    if (!Number.isFinite(value)) return 0;
    return value > 0 ? Math.floor(value) : 0;
  }

  if (typeof value === "string") {
    const raw = value.trim().toLowerCase();
    if (raw === "" || raw === "false" || raw === "0" || raw === "no" || raw === "off") {
      return 0;
    }
    if (raw === "true" || raw === "yes" || raw === "on") {
      return CASHIER_2ND_LEGACY_QUOTA;
    }
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) {
      return parsed > 0 ? Math.floor(parsed) : 0;
    }
    return CASHIER_2ND_LEGACY_QUOTA;
  }

  return CASHIER_2ND_LEGACY_QUOTA;
}

export function isAlive(lastSeen: unknown, now: number): boolean {
  const ts = toTimestamp(lastSeen);
  if (ts == null) return false;
  return now - ts < SEAT_STALE_MS;
}

/**
 * ตัดสินการจองที่นั่ง — callback ของ runTransaction เรียกตัวนี้ตรง ๆ
 *
 * ค่า null ต้องได้ ok เสมอ: รอบแรกของ RTDB transaction อาจได้ null จาก cache ที่ยังว่าง
 * ถ้า abort ตรงนั้นจะไม่มีการถาม server เลย
 */
export function decideClaim(seats: any, opts: ClaimOptions): ClaimDecision {
  const { mode, me, name, quota, now } = opts;
  const main = readMain(seats);
  const second = readSecond(seats);

  const holdersOf = (record: Record<string, any>) =>
    Object.keys(record)
      .map((id) => toHolder(id, record[id], now))
      .sort((a, b) => (b.lastSeen ?? 0) - (a.lastSeen ?? 0));

  if (mode === SEAT_MODE_MAIN) {
    const used = Object.keys(second).filter((id) => isAlive(second[id]?.lastSeen, now)).length;

    if (main && main.deviceId !== me && !opts.force) {
      return {
        result: "main_taken",
        next: null,
        holders: [toHolder(main.deviceId, main, now)],
        used,
        quota,
      };
    }

    // เครื่องเดียวเป็นได้บทบาทเดียว — เปลี่ยนจาก Second มาเป็น Main ต้องคืนที่ Second
    delete second[me];
    const kicks = readKicks(seats);
    delete kicks[me];
    // แย่งจากเครื่องอื่น → จดไว้ว่าใครแย่ง เครื่องที่โดนจะได้เห็น Computer ID เรา
    if (main && main.deviceId !== me) {
      kicks[main.deviceId] = { byId: me, byName: name, mode: SEAT_MODE_MAIN, at: now };
    }
    // เข้าซ้ำเครื่องเดิมไม่รีเซ็ตเวลาเข้าใช้
    const claimedAt = main && main.deviceId === me ? toTimestamp(main.claimedAt) ?? now : now;
    return {
      result: "ok",
      next: build({ deviceId: me, name, claimedAt, lastSeen: now }, second, kicks, now),
      holders: [],
      used: Object.keys(second).filter((id) => isAlive(second[id]?.lastSeen, now)).length,
      quota,
    };
  }

  // ---- Second ----
  if (quota <= 0) {
    return { result: "no_quota", next: null, holders: holdersOf(second), used: 0, quota };
  }

  // เครื่องที่หลุดเกิน SEAT_STALE_MS ไม่นับ และล้างทิ้งในรอบนี้เลย (ยกเว้นตัวเอง)
  for (const id of Object.keys(second)) {
    if (id !== me && !isAlive(second[id]?.lastSeen, now)) delete second[id];
  }

  const kicks = readKicks(seats);
  delete kicks[me];
  if (opts.kickId && opts.kickId !== me && second[opts.kickId]) {
    delete second[opts.kickId];
    kicks[opts.kickId] = { byId: me, byName: name, mode: SEAT_MODE_SECOND, at: now };
  }

  if (!second[me] && Object.keys(second).length >= quota) {
    return {
      result: "second_full",
      next: null,
      holders: holdersOf(second),
      used: Object.keys(second).length,
      quota,
    };
  }

  second[me] = { name, claimedAt: toTimestamp(second[me]?.claimedAt) ?? now, lastSeen: now };
  const nextMain = main && main.deviceId === me ? null : main;
  return {
    result: "ok",
    next: build(nextMain, second, kicks, now),
    holders: [],
    used: Object.keys(second).length,
    quota,
  };
}

/**
 * เครื่องนี้ยังถือที่นั่งอยู่ไหม
 *
 * - held    = ยังเป็นของเรา
 * - lost    = Main เป็นของเครื่องอื่นแล้ว (โดนย้าย)
 * - missing = ไม่มีชื่อเรา แต่ก็ไม่มีใครแทนตรง ๆ → ลองจองใหม่ได้
 *             (Second ที่โดนเตะก็เข้าเคสนี้ ถ้าเต็มอยู่ จองใหม่จะไม่ผ่านเอง)
 */
export function checkHolding(seats: any, mode: SeatMode, me: string): "held" | "lost" | "missing" {
  if (mode === SEAT_MODE_MAIN) {
    const main = readMain(seats);
    if (!main) return "missing";
    return main.deviceId === me ? "held" : "lost";
  }
  return readSecond(seats)[me] ? "held" : "missing";
}

/**
 * แย่งที่นั่ง Second จากเครื่อง kickId (ผู้ใช้เลือกจากหน้า ask-2nd-cashier-override)
 *
 * สลับในรอบเดียว: เอา kickId ออก ใส่ตัวเองแทน จำนวนเครื่องเท่าเดิม จึงไม่ต้องรู้โควตา
 * - kickId ไม่อยู่แล้ว (ออกไปเอง / โดนคนอื่นเตะไปก่อน) → not_found ไม่เขียนอะไร
 *   ให้ seatSecond จองตามโควตาตามปกติแทน
 * - เครื่องนี้มีที่อยู่แล้ว → ok ไม่ต้องเตะใคร
 */
export function decideSwapSecond(
  seats: any,
  opts: { me: string; name: string; kickId: string; now: number }
): { result: "ok" | "not_found"; next: any } {
  const { me, name, kickId, now } = opts;
  const main = readMain(seats);
  const second = readSecond(seats);
  const kicks = readKicks(seats);

  if (second[me]) {
    second[me] = { ...second[me], name, lastSeen: now };
    return { result: "ok", next: build(main, second, kicks, now) };
  }
  if (!kickId || kickId === me || !second[kickId]) return { result: "not_found", next: null };

  delete second[kickId];
  delete kicks[me];
  kicks[kickId] = { byId: me, byName: name, mode: SEAT_MODE_SECOND, at: now };
  second[me] = { name, claimedAt: now, lastSeen: now };
  const nextMain = main && main.deviceId === me ? null : main;
  return { result: "ok", next: build(nextMain, second, kicks, now) };
}

/**
 * คืนที่นั่งทุกตำแหน่งของเครื่องนี้
 * คืน null = ไม่มีอะไรต้องเปลี่ยน (ไม่ต้องเขียน)
 */
export function decideRelease(seats: any, me: string): any | null {
  const main = readMain(seats);
  const second = readSecond(seats);
  const ownsMain = !!main && main.deviceId === me;
  const ownsSecond = !!second[me];
  if (!ownsMain && !ownsSecond) return null;

  delete second[me];
  const kicks = readKicks(seats);
  delete kicks[me];
  return build(ownsMain ? null : main, second, kicks, null);
}

/** ใครแย่ง / เตะเครื่องนี้ออก (null = ไม่มีบันทึก) */
export function readKick(seats: any, me: string): SeatKick | null {
  return readKicks(seats)[me] ?? null;
}

/** สรุปที่นั่งไว้โชว์ในหน้าเลือกโหมด */
export function summarizeSeats(seats: any, now: number) {
  const main = readMain(seats);
  const second = readSecond(seats);
  const seconds = Object.keys(second).map((id) => toHolder(id, second[id], now));
  return {
    main: main ? toHolder(main.deviceId, main, now) : null,
    seconds,
    secondUsed: seconds.filter((s) => s.alive).length,
  };
}

export interface SeatScopeInput {
  /** หน้า meta freeAuth (login / login-with-token / signup / forgot-password) */
  freeAuthPage: boolean;
  /** อยู่ที่ app2 (โดเมน beta) */
  onBetaSite: boolean;
  localhost: boolean;
  /** ร้านนี้อยู่ใน beta_whitelist ไหม — เรียกเฉพาะตอนต้องรู้จริง (อ่าน Firestore) */
  isWhitelisted: () => Promise<boolean>;
}

/**
 * true = รอบนี้ไม่ต้องตรวจ / จองที่นั่ง
 *
 * - หน้า freeAuth: ยังไม่ได้ใช้ POS จริง login เสร็จจะ reload ไป "/" แล้วค่อยตรวจ
 *   (logout ที่ app2 ส่งมา /login?signout=1 ของ app1 ซึ่งยังมี session ค้าง)
 * - app1 + ร้าน beta: แค่ทางผ่านไป app2 Computer ID เก็บใน localStorage แยกตามโดเมน
 *   เครื่องเดียวกันจึงเป็นคนละ ID ถ้า app1 จองด้วย จะแย่งที่นั่งกับตัวเองบน app2
 * - localhost ไม่ข้าม (เหมือน fetchBetaWhiteLists) ทดสอบกับร้าน beta ได้
 * - อ่าน whitelist ไม่ได้ → ตรวจตามปกติ
 */
export async function decideSkipSeatCheck(input: SeatScopeInput): Promise<boolean> {
  if (input.freeAuthPage) return true;
  if (input.onBetaSite || input.localhost) return false;
  try {
    return (await input.isWhitelisted()) === true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------

function readMain(seats: any): { deviceId: string; name?: string; lastSeen?: any } | null {
  const main = isPlainObject(seats) ? seats.main : null;
  if (!isPlainObject(main) || typeof main.deviceId !== "string" || !main.deviceId) return null;
  return { ...main };
}

/**
 * แถวที่ไม่มี name ไม่นับเป็นที่นั่ง — heartbeat ที่ค้างคิวตอนออฟไลน์แล้วไปเขียน
 * `second/{me}/lastSeen` หลังโดนเตะ จะสร้างแถวครึ่ง ๆ ที่มีแต่ lastSeen ขึ้นมาใหม่
 * ถ้านับแถวนั้น เครื่องที่โดนเตะจะกลับมาถือที่นั่งได้เอง (transaction ถัดไปล้างทิ้งให้)
 */
function readSecond(seats: any): Record<string, any> {
  const record = toRecord(isPlainObject(seats) ? seats.second : null);
  for (const id of Object.keys(record)) {
    if (!isPlainObject(record[id]) || typeof record[id].name !== "string") delete record[id];
  }
  return record;
}

function readKicks(seats: any): Record<string, SeatKick> {
  const record = toRecord(isPlainObject(seats) ? seats.kicks : null);
  for (const id of Object.keys(record)) {
    const kick = record[id];
    if (!isPlainObject(kick) || typeof kick.byId !== "string") delete record[id];
  }
  return record;
}

/** now = null ไม่ตัดบันทึกเก่า (ตอนคืนที่นั่งไม่รู้เวลา server ก็ไม่เป็นไร) */
function build(main: any, second: Record<string, any>, kicks: Record<string, SeatKick>, now: number | null) {
  if (now != null) {
    for (const id of Object.keys(kicks)) {
      const at = toTimestamp(kicks[id]?.at);
      if (at == null || now - at > SEAT_KICK_TTL_MS) delete kicks[id];
    }
  }
  // *Iso สร้างใหม่จากตัวเลขทุกครั้งที่เขียน ไม่งั้นข้อความค้างไม่ตรงกับตัวเลข
  for (const id of Object.keys(second)) second[id] = withSeatIso(second[id]);
  for (const id of Object.keys(kicks)) kicks[id] = { ...kicks[id], atIso: toLocalIso(toTimestamp(kicks[id].at)) };
  return {
    main: main ? withSeatIso(main) : null,
    second: Object.keys(second).length ? second : null,
    kicks: Object.keys(kicks).length ? kicks : null,
  };
}

function withSeatIso(entry: any) {
  return {
    ...entry,
    claimedAtIso: toLocalIso(toTimestamp(entry?.claimedAt)),
    lastSeenIso: toLocalIso(toTimestamp(entry?.lastSeen)),
  };
}

/**
 * epoch ms → ISO ตามเวลาเครื่อง เช่น 2026-10-20T02:45:08+07:00 (แบบเดียวกับ dayjs().format())
 * เขียนเองเพราะไฟล์นี้ import อะไรไม่ได้ — null = ไม่มีเวลา
 */
export function toLocalIso(ms: number | null): string | null {
  if (ms == null || !Number.isFinite(ms)) return null;
  const d = new Date(ms);
  const offset = -d.getTimezoneOffset();
  const pad = (n: number) => String(Math.floor(Math.abs(n))).padStart(2, "0");
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  return `${date}T${time}${offset < 0 ? "-" : "+"}${pad(offset / 60)}:${pad(offset % 60)}`;
}

function toHolder(deviceId: string, entry: any, now: number): SeatHolder {
  const lastSeen = toTimestamp(entry?.lastSeen);
  return {
    deviceId,
    name: typeof entry?.name === "string" && entry.name ? entry.name : deviceId,
    claimedAt: toTimestamp(entry?.claimedAt),
    lastSeen,
    alive: isAlive(lastSeen, now),
  };
}

function toTimestamp(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value) {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return null;
}

function isPlainObject(value: any): boolean {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

/**
 * RTDB คืน node ที่คีย์เป็นเลขล้วนมาเป็น array ให้เอง ปกติ deviceId เป็น nanoid
 * จึงไม่เจอ แต่หน้า settings/computer-name ให้พิมพ์ id เองได้ (เช่น "1", "2")
 */
function toRecord(value: any): Record<string, any> {
  if (Array.isArray(value)) {
    const out: Record<string, any> = {};
    value.forEach((item, index) => {
      if (item != null) out[String(index)] = item;
    });
    return out;
  }
  return isPlainObject(value) ? { ...value } : {};
}
